// Top "Call Information" card — agent / duration / date / type grid, then
// from/to/direction, then optional customer info.
import React from 'react';
import { Phone, Clock, Calendar, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCallType, formatPhoneNumber, isMeaningfulValue } from './callDetailUtils';

const InfoStat = ({ icon: Icon, label, value }) => (
  <div className="flex items-start space-x-3">
    <Icon className="w-5 h-5 text-slate-400 mt-1" />
    <div>
      <p className="text-sm text-slate-500">{label}</p>
      <p className="font-medium text-slate-900">{value}</p>
    </div>
  </div>
);

const agentDisplay = (call) =>
  call.agent_name && call.agent_name !== call.agent_id
    ? `${call.agent_name} (ext ${call.extension || call.agent_id})`
    : `Ext ${call.extension || call.agent_id}`;

const durationDisplay = (call) =>
  `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s`;

const dateDisplay = (call) =>
  new Date(call.call_date).toLocaleString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

const FromTo = ({ call }) => {
  const inbound = call.call_direction === 'inbound';
  const fromValue = inbound
    ? (formatPhoneNumber(call.customer_phone) || 'Unknown')
    : (call.agent_id || 'Unknown');
  const toValue = inbound
    ? (call.agent_id || 'Unknown')
    : (formatPhoneNumber(call.customer_phone) || 'Unknown');
  return (
    <div className="mt-6 pt-6 border-t border-slate-200">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
        <div>
          <p className="text-sm text-slate-500">From</p>
          <p className="font-medium text-slate-900 font-mono text-sm">{fromValue}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">To</p>
          <p className="font-medium text-slate-900 font-mono text-sm">{toValue}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">Direction</p>
          <p className="font-medium text-slate-900 capitalize">{call.call_direction}</p>
        </div>
      </div>
    </div>
  );
};

const CustomerInfo = ({ call }) => (
  <div className="mt-6 pt-6 border-t border-slate-200">
    <p className="text-sm text-slate-500 mb-2">Customer Information</p>
    <div className="flex items-center space-x-6">
      {isMeaningfulValue(call.customer_name) && (
        <div>
          <p className="text-sm text-slate-500">Name</p>
          <p className="font-medium text-slate-900">{call.customer_name}</p>
        </div>
      )}
      {isMeaningfulValue(call.customer_phone) && (
        <div>
          <p className="text-sm text-slate-500">Phone</p>
          <p className="font-medium text-slate-900">{formatPhoneNumber(call.customer_phone)}</p>
        </div>
      )}
    </div>
  </div>
);

export const CallInfoCard = ({ call }) => {
  const hasCustomerInfo =
    isMeaningfulValue(call.customer_name) || isMeaningfulValue(call.customer_phone);
  return (
    <Card className="border-slate-200">
      <CardHeader>
        <CardTitle>Call Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <InfoStat icon={Phone} label="Agent" value={agentDisplay(call)} />
          <InfoStat icon={Clock} label="Duration" value={durationDisplay(call)} />
          <InfoStat icon={Calendar} label="Date & Time" value={dateDisplay(call)} />
          <InfoStat icon={TrendingUp} label="Type" value={formatCallType(call.call_type)} />
        </div>
        <FromTo call={call} />
        {hasCustomerInfo && <CustomerInfo call={call} />}
      </CardContent>
    </Card>
  );
};
